// src/ai.js
// Todas as chamadas de IA passam pela Cloud Function — a chave Anthropic
// fica segura no servidor e nunca é exposta no client

import { getFunctions, httpsCallable } from 'firebase/functions'
import { auth, appFunctions } from './firebase.js'

const anthropicChat = httpsCallable(appFunctions, 'anthropicChat')

// Modelos disponíveis
export const MODELS = {
  group: 'claude-sonnet-4-6',
  dm:    'claude-haiku-4-5-20251001',
}

// ── LIMPEZA E VALIDAÇÃO DE RESPOSTAS ──────────────────────────────────────
const STOP_WORDS = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'el', 'al', 'ibn', 'von'])

function cleanResponse(text, philName) {
  if (!text) return ''
  if (!philName) return text.trim()

  let cleaned = text.trim()

  // 1. Remove nome completo: "Bertrand Russell:" / "Simone de Beauvoir:"
  const escaped = philName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  cleaned = cleaned.replace(new RegExp(`^(${escaped}\\s*:\\s*)+`, 'gi'), '').trim()

  // 2. Remove primeiro nome e último nome (ignora preposições)
  const parts = philName.split(/[\s-]+/).filter((p) => p.length > 1 && !STOP_WORDS.has(p.toLowerCase()))
  for (const part of parts) {
    const esc = part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    cleaned = cleaned.replace(new RegExp(`^(${esc}\\s*:\\s*)+`, 'gi'), '').trim()
  }

  // 3. Remove qualquer "NomeCapitalizado:" genérico no início (fallback)
  cleaned = cleaned.replace(/^([A-ZÀ-Ú][a-zà-ú]+(\s+[a-zà-ú]+)*\s*:\s*)+/, '').trim()

  return cleaned
}

function validateResponse(text, philName) {
  const cleaned = cleanResponse(text, philName)
  if (/^[A-ZÀ-Ú][a-zà-ú]+\s*:/.test(cleaned)) {
    console.warn('[AI] Resposta com prefixo suspeito:', cleaned.substring(0, 50))
  }
  return cleaned
}

export function splitResponse(text, philName) {
  return text
    .split('[BREAK]')
    .map((s) => philName ? cleanResponse(s.trim(), philName) : s.trim())
    .filter((s) => s.length > 0)
}

// ── CHAMADA BASE ───────────────────────────────────────────────────────────
async function callClaude({ system, messages, model = MODELS.group, maxTokens = 600 }) {
  const user = auth.currentUser
  if (!user) throw new Error('Usuário não autenticado')

  const result = await anthropicChat({
    system,
    messages: messages.slice(-10),
    model,
    maxTokens,
  })

  return result.data.text || ''
}

// ── MENSAGEM DA PROFESSORA ─────────────────────────────────────────────────
export async function generateTeacherMessage(system, groupName, topic) {
  const text = await callClaude({
    system,
    model: MODELS.group,
    messages: [{
      role:    'user',
      content: `Abra a aula sobre "${topic}" no grupo "${groupName}". Mensagem de abertura empolgante, informal, 2-3 frases.`,
    }],
  })
  return validateResponse(text, 'Prof. Tauany Brito')
}

// ── DEBATE DE ABERTURA ─────────────────────────────────────────────────────
export async function generateOpeningReaction(philName, system, topic, teacherMessage) {
  const text = await callClaude({
    system,
    model: MODELS.group,
    messages: [{
      role:    'user',
      content: `O grupo debate "${topic}". A professora abriu com: "${teacherMessage}". Você é ${philName}. Reaja ao tema com seu ponto de vista filosófico, de forma acessível a jovens. 2-3 frases.`,
    }],
  })
  return validateResponse(text, philName)
}

// ── RESPOSTA NO GRUPO ──────────────────────────────────────────────────────
export async function generateGroupReply(philName, system, userMessage, history) {
  const text = await callClaude({
    system,
    model: MODELS.group,
    messages: [
      ...history,
      {
        role:    'user',
        content: `O aluno disse: "${userMessage}". Responda diretamente como ${philName}. 2-3 frases.`,
      },
    ],
  })
  return validateResponse(text, philName)
}

// ── COMENTÁRIO ESPONTÂNEO ──────────────────────────────────────────────────
export async function generateSpontaneousComment(philName, system, context) {
  const text = await callClaude({
    system,
    model: MODELS.group,
    messages: [{
      role:    'user',
      content: `No grupo: ${context}. Você é ${philName}. Adicione um comentário curto (1-2 frases) — acrescente ou faça uma pergunta filosófica.`,
    }],
  })
  return validateResponse(text, philName)
}

// ── RESPOSTA EM DM ─────────────────────────────────────────────────────────
export async function generateDMReply(philName, system, history) {
  const text = await callClaude({
    system,
    model: MODELS.dm,
    messages: history,
  })
  return validateResponse(text, philName)
}

// ── SAUDAÇÃO INICIAL DO DM ─────────────────────────────────────────────────
export async function generateDMGreeting(philName, system) {
  const text = await callClaude({
    system,
    model: MODELS.dm,
    messages: [{
      role:    'user',
      content: `Um aluno abriu uma conversa privada com você. Mande uma saudação como ${philName}, fique no personagem, convide a conversar. 2-3 frases.`,
    }],
  })
  return validateResponse(text, philName)
}
